/**
 * Truncate an address or string, preserving a total of `length` characters.
 * Hexadecimal addresses maintain their `0x` prefix when using 'middle' position.
 * Short values are returned as-is without truncation.
 *
 * @param value - The value to truncate.
 * @param length - The total number of characters to preserve (default `8`).
 * @param position - Where to truncate: 'start' (keep end), 'middle' (keep both ends), or 'end' (keep start). Default 'middle'.
 */
export function truncateAddress(
  value: string,
  length: number = 8,
  position: 'start' | 'middle' | 'end' = 'middle'
): string {
  // Calculate minimum output length to avoid making strings longer
  // Hex addresses in middle position: 0x + half + … + half = length + 3
  // All other cases: length + 1 (for the ellipsis)
  const minOutputLength =
    position === 'middle' && value.startsWith('0x') ? length + 3 : length + 1;

  if (value.length <= minOutputLength) {
    return value;
  }

  switch (position) {
    case 'start': {
      // Keep last part, truncate start
      return `…${value.slice(-length)}`;
    }
    case 'end': {
      // Keep first part, truncate end
      return `${value.slice(0, length)}…`;
    }
    case 'middle':
    default: {
      // Keep start + end halves
      const half = Math.floor(length / 2);

      if (value.startsWith('0x')) {
        const start = value.slice(2, half + 2);
        const end = value.slice(-half);
        return `0x${start}…${end}`;
      }

      const start = value.slice(0, half);
      const end = value.slice(-half);
      return `${start}…${end}`;
    }
  }
}

type InitialsOptions = {
  /**
   * The maximum number of letters to return.
   * @default 2
   */
  maxLetters?: number;
  /**
   * The locale to use for the initials.
   * @default undefined (system locale)
   */
  locale?: string;
};

/**
 * Returns the appropriate initials for a name.
 *
 * @example
 * formatInitials('John Doe') // "JD"
 * formatInitials('John Ronald Reuel Tolkien') // "JT"
 * formatInitials('infinex') // "IN"
 */
export function formatInitials(name: string, options: InitialsOptions = {}) {
  const { maxLetters = 2, locale } = options;

  if (maxLetters < 1) {
    throw new TypeError('maxLetters must be at least 1');
  }

  name = name.trim();

  if (!name) return '?';

  const cleaned = name.replace(/\s+/gu, ' ');
  const words = cleaned.split(/\s+/u).filter(Boolean);

  const segmenter =
    typeof Intl !== 'undefined' && 'Segmenter' in Intl
      ? new Intl.Segmenter(locale, { granularity: 'grapheme' })
      : null;

  const getGraphemes = (value: string): string[] => {
    if (!segmenter) return Array.from(value); // fallback
    return Array.from(segmenter.segment(value), (s) => s.segment);
  };

  const toUpper = (value: string) =>
    locale ? value.toLocaleUpperCase(locale) : value.toUpperCase();

  // single word: grab the first N letters
  // "Infinex" -> "IN"
  if (words.length === 1) {
    const graphemes = getGraphemes(words[0] ?? '');
    return toUpper(graphemes.slice(0, maxLetters).join(''));
  }

  // max 2: use first and last words
  // "John Henry Doe" -> "JD"
  if (maxLetters === 2) {
    const first = getGraphemes(words[0]!)[0]!;
    const last = getGraphemes(words[words.length - 1]!)[0]!;
    return toUpper((first + last).slice(0, maxLetters));
  }

  // max 3+: first letter of each word, from first to last
  // "John Henry Doe" -> "JHD"
  return Array.from({ length: maxLetters })
    .map((_, index) => {
      const graphemes = getGraphemes(words[index] ?? '');
      return toUpper(graphemes[0] ?? '');
    })
    .join('');
}
